/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Divider, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { Controller, useForm } from "react-hook-form";
import CustomModal from "../../../Core/Components/Modal/CustomModal";
import CustomRoundedImage from "../../../Core/Components/CustomRoundedImage";
import CloseIcon from "../../../Common/Svgs/CloseIcon";
import Button from "../../../Core/Components/CustomButton";
import { updateBatteries } from "../../../Redux/Slices/batteries.slice";
import AlertIcon from "../../../Common/Svgs/AlertIcon";
import { client } from "../../../Utils/axiosClient";
import { getBatteryV3 } from "../../../Redux/APIs/batteriesAPI";
import AddUserIcon from "../../../Common/Svgs/AddUserIcon";
import { PopoverDropdown } from "../../../Core/Components/CustomDropdown";

const AssignPickupAgentModal = ({ visible, onCancel }) => {
  const dispatch = useDispatch();

  const { assignPickupAgentModal } = useSelector((state) => state.batteries);

  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [disabled, setDisabled] = useState("");

  const { control, formState, setValue, clearErrors } = useForm({
    defaultValues: {
      hubId: assignPickupAgentModal?.data?.hubId || null,
      hubName: assignPickupAgentModal?.data?.hubName || "",
      agentId: selectedAgent?.id || null,
      agentName: selectedAgent?.value || "",
    },
  });

  const getAgents = useCallback(async () => {
    if (visible && assignPickupAgentModal?.data?.receiverHubId) {
      try {
        setLoading(true);
        const response = await client.get(
          `${process.env.REACT_APP_API_URL}/hub-station/hub-agents`,
          {
            params: {
              hubId: assignPickupAgentModal?.data?.receiverHubId,
            },
          },
        );
        setAgents(
          response.data.map((agent) => {
            return { id: agent.id, value: agent.name };
          }) || [],
        );
      } catch (error) {
        console.log("Failed to fetch hub agents:", error);
      } finally {
        setLoading(false);
      }
    }
  }, [visible, assignPickupAgentModal?.data?.receiverHubId]);

  const handleCancel = () => {
    setSelectedAgent(null);
    setDisabled("");
    onCancel();
  };

  const handleConfirm = async () => {
    if (!selectedAgent) {
      setDisabled("Please assign a pickup hub agent.");
      return;
    }

    try {
      setDisabled("");
      setConfirmLoading(true);

      const payload = {
        requestId: parseInt(assignPickupAgentModal?.data?.requestId, 10),
        agentId: parseInt(selectedAgent?.id, 10),
      };

      const response = await client.post(
        `${process.env.REACT_APP_API_URL}/battery/assign-pickup-agent`,
        payload,
      );

      if (response.status === 200) {
        dispatch(
          updateBatteries({
            key: "showToastMessage",
            value: {
              visible: true,
              data: {
                title: "Pickup agent assigned.",
                description: "The agent will collect the batteries soon.",
              },
            },
          }),
        );

        await dispatch(getBatteryV3());
      }
    } catch (error) {
      console.log(error);
    } finally {
      setConfirmLoading(false);
      setDisabled("");
      setSelectedAgent(null);
      onCancel();
    }
  };

  useEffect(() => {
    getAgents();
  }, [getAgents]);

  useEffect(() => {
    if (visible && assignPickupAgentModal?.data) {
      setValue("hubId", assignPickupAgentModal.data.hubId || assignPickupAgentModal.data.receiverHubId || null);
      setValue("hubName", assignPickupAgentModal.data.hubName || assignPickupAgentModal.data.receiverHubName || "");
      setValue("agentId", null);
      setValue("agentName", "");
      setSelectedAgent(null);
      clearErrors("agentName");
    }
  }, [visible, assignPickupAgentModal, setValue, clearErrors]);

  return (
    <CustomModal visible={visible} onCancel={handleCancel} width="536px">
      <div
        className="rounded-xl bg-background-white w-full"
        style={{
          boxShadow:
            "12px 12px 16px -4px rgba(16, 24, 40, 0.08), -4px -4px 6px -2px rgba(16, 24, 40, 0.03)",
        }}
      >
        <div className="flex items-center justify-between pl-4 pr-6 pt-4">
          <div className="flex items-center gap-x-3">
            <CustomRoundedImage
              size="w-12 h-12"
              image={<AddUserIcon className="text-white" />}
              background="bg-primary"
            />

            <h1 className="font-poppins text-base font-medium">Assign Agent</h1>
          </div>

          <button type="button" onClick={handleCancel}>
            <CloseIcon className="w-6 h-6 stroke-dark-gray" />
          </button>
        </div>

        <div className="flex">
          <div className="px-6 mt-6 pb-6 w-[236px]">
            <div className="flex items-center gap-x-2">
              <p className="font-aileron font-medium text-xs text-black capitalize whitespace-nowrap">
                {assignPickupAgentModal?.data?.supplierHubName ||
                  "Supplier Hub"}
              </p>
            </div>
            <div className="flex flex-col items-start gap-y-2 w-[137px] mt-1">
              <p className="font-aileron font-normal text-xs text-dark-gray capitalize">
                Collecting from
              </p>
            </div>
          </div>
          <div className="px-6 mt-6 pb-6 w-[236px]">
            <div className="flex items-center gap-x-2">
              <p className="font-aileron font-medium text-xs text-black capitalize whitespace-nowrap">
                {assignPickupAgentModal?.data?.batteryCount || "2"}
              </p>
            </div>
            <div className="flex flex-col items-start gap-y-2 w-[137px] mt-1">
              <p className="font-aileron font-normal text-xs text-dark-gray capitalize">
                No. of batteries
              </p>
            </div>
          </div>
        </div>

        <div className="mx-6 my-0 w-[488px]">
          <Divider className="m-0" />
        </div>

        <div className="px-6 mt-6 pb-6">
          {loading ? (
            <div className="flex items-center justify-center my-10">
              <Spin
                spinning={loading}
                indicator={<LoadingOutlined size="large" />}
              />
            </div>
          ) : (
            <div className="flex justify-between">
              <div>
                <p className="font-aileron font-normal text-xs leading-none text-dark-gray mb-2">
                  My Hub Station
                </p>

                <div
                  role="button"
                  className="px-3.5 py-2 w-[236px] flex items-center justify-between border rounded-md bg-background cursor-not-allowed"
                >
                  <p className="font-aileron text-xs capitalize text-dark-gray">
                    {assignPickupAgentModal?.data?.hubName || "Receiver Hub"}
                  </p>
                </div>
              </div>

              <div>
                <p className="font-aileron font-normal text-xs leading-none text-dark-gray mb-2">
                  Technical Agent
                </p>

                <Controller
                  name="agentName"
                  control={control}
                  rules={{
                    required: "Required.",
                  }}
                  render={({ field }) => (
                    <PopoverDropdown
                      {...field}
                      enableSearch
                      placeholder="Assign Technical Agent"
                      searchPlaceholder="Search Agent"
                      className="max-h-[300px] overflow-y-scroll"
                      valueStyle={{
                        width: "236px",
                      }}
                      formState={formState}
                      options={agents}
                      onSelect={(data) => {
                        setValue("agentName", data.value);
                        setValue("agentId", data.id);
                        clearErrors("agentName");
                        setSelectedAgent(data);
                      }}
                    />
                  )}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-x-3 px-6 pb-6">
          {disabled ? (
            <div className="flex items-start gap-x-2">
              <AlertIcon size={16} className="text-alert-red" />

              <p className="font-aileron text-xs font-medium text-alert-red">
                {disabled}
              </p>
            </div>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-x-3">
            <Button colors="grey" size="primary" onClick={handleCancel}>
              Cancel
            </Button>

            <Button
              colors="primary"
              size="primary"
              loading={confirmLoading}
              disabled={confirmLoading}
              onClick={handleConfirm}
            >
              Assign Agent
            </Button>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default AssignPickupAgentModal;
